"use client"

import { createContext, useContext, useEffect, useState, useCallback } from "react"
import { Language, translations } from "./translations"
import { useAuth } from "@/lib/auth-provider"
import { getApiUrl } from "@/lib/api-url"

interface LanguageContextType {
  language: Language
  setLanguage: (language: Language) => void
  updateUserLanguagePreference: (language: Language) => Promise<void>
  t: (key: string, variables?: Record<string, string | number>) => string
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined)

export function LanguageProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const [language, setLanguageState] = useState<Language>("en")
  const [mounted, setMounted] = useState(false)
  
  // Get auth context - use try/catch in case it's not available
  let user = null
  let profile = null
  let session = null
  try {
    const auth = useAuth()
    user = auth?.user
    profile = auth?.profile
    session = auth?.session
  } catch (e) {
    // Auth provider not available, continue without it
  }
  
  // Effet pour éviter les erreurs d'hydratation et charger la langue
  useEffect(() => {
    const loadLanguage = async () => {
      // Priority 1: localStorage (source de vérité pour l'utilisateur)
      const storedLanguage = localStorage.getItem("language") as Language
      if (storedLanguage && (storedLanguage === "fr" || storedLanguage === "en")) {
        setLanguageState(storedLanguage)
      } 
      // Priority 2: User profile preference (si rien dans localStorage)
      else if (profile?.preferred_language) {
        setLanguageState(profile.preferred_language)
        localStorage.setItem("language", profile.preferred_language)
      } 
      // Priority 3: Default to English
      else {
        setLanguageState("en")
        localStorage.setItem("language", "en")
      }
      setMounted(true)
    }
    
    loadLanguage()
  }, [profile?.preferred_language])

  // Function to update language and save to user profile if logged in
  const setLanguage = async (newLanguage: Language) => {
    setLanguageState(newLanguage)
    localStorage.setItem("language", newLanguage)
    
    // If user is logged in, update their profile
    if (user?.id && session?.access_token) {
      try {
        await fetch(getApiUrl('/api/profile/language'), {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ language: newLanguage }),
        })
      } catch (error) {
        // console.error('Failed to update language preference:', error)
      }
    }
  }

  // Separate function to explicitly update user language preference
  const updateUserLanguagePreference = async (newLanguage: Language) => {
    if (user?.id && session?.access_token) {
      await fetch(getApiUrl('/api/profile/language'), {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ language: newLanguage }),
      })

      setLanguageState(newLanguage)
      localStorage.setItem("language", newLanguage)
    } else {
      // If not logged in, just update localStorage
      setLanguageState(newLanguage)
      localStorage.setItem("language", newLanguage)
    }
  }

  // Fonction de traduction (memoized to prevent infinite re-renders in consumers)
  const t = useCallback((key: string, variables?: Record<string, string | number>): string => {
    const translation = translations[key]
    if (translation) {
      let text = translation[language] || key
      
      // Remplacer les variables si elles sont fournies
      if (variables) {
        Object.entries(variables).forEach(([varName, varValue]) => {
          text = text.replace(`{${varName}}`, String(varValue))
        })
      }
      
      return text
    }
    return key
  }, [language])

  return (
    <LanguageContext.Provider value={{ language, setLanguage, updateUserLanguagePreference, t }}>
      {children}
    </LanguageContext.Provider>
  )
}

export function useLanguage() {
  const context = useContext(LanguageContext)
  if (context === undefined) {
    throw new Error("useLanguage doit être utilisé à l'intérieur d'un LanguageProvider")
  }
  return context
} 